import { maxBy } from "lodash";

import { BaseLogger } from "../base_logger";
import { PublishableActionType, PubsubStatsCollectorType } from "../shared/types";

// If ping time spikes above POOR_CONNECTION_EXTREME_PING_THRESHOLD, the poor connection indicator will light up for
// that user
const POOR_CONNECTION_EXTREME_PING_THRESHOLD = 5000;
// If ping spikes above POOR_CONNECTION_MAX_PING_THRESHOLD and the
// median of the last 5 ping times (measured every Settings.PUBSUB_PING_INTERVAL) is greater than
// POOR_CONNECTION_MEDIAN_PING_THRESHOLD, the poor connection indicator will light up for that user
const POOR_CONNECTION_MAX_PING_THRESHOLD = 2500;
const POOR_CONNECTION_MEDIAN_PING_THRESHOLD = 1000;
// If the max ping is less than GOOD_CONNECTION_MAX_PING_THRESHOLD and the median ping time is less than
// GOOD_CONNECTION_MEDIAN_PING_THRESHOLD, remove the poor connection indicator for that user.
const GOOD_CONNECTION_MAX_PING_THRESHOLD = 2000;
const GOOD_CONNECTION_MEDIAN_PING_THRESHOLD = 1000;
const ROLLING_PING_RTT_LENGTH = 5;

function median(arr: number[]): number | undefined {
  if (!arr.length) return undefined;
  const s = [...arr].sort((a, b) => a - b);
  const mid = Math.floor(s.length / 2);
  return s.length % 2 === 0 ? (s[mid - 1] + s[mid]) / 2 : s[mid];
}

export class PubsubStatsCollector extends BaseLogger implements PubsubStatsCollectorType {
  private msgCount: { [key: string]: number } = {};

  private msgRTT: { [key: string]: number[] } = {};

  private pingRTT: number[] = [];

  private rollingPingRTT: number[] = [];

  public resetStats() {
    this.msgCount = {};
    this.msgRTT = {};
    this.pingRTT = [];
  }

  public addRecord(action: PublishableActionType) {
    const { type, meta } = action;
    const { clientTimestamp } = meta;
    if (!clientTimestamp) {
      return;
    }
    const rtt = Date.now() - clientTimestamp;
    if (this.msgCount[type] === undefined) {
      this.msgCount[type] = 0;
      this.msgRTT[type] = [];
    }
    this.msgCount[type] += 1;
    this.msgRTT[type].push(rtt);
  }

  public addPingRecord(rtt: number) {
    this.pingRTT.push(rtt);
    this.rollingPingRTT.push(rtt);
    if (this.rollingPingRTT.length > ROLLING_PING_RTT_LENGTH) {
      this.rollingPingRTT.shift();
    }
  }

  public getStats() {
    const allRTTs = Object.values(this.msgRTT).flat();
    return {
      msgCount: Object.values(this.msgCount).reduce(
        (partialSum, currentValue) => partialSum + currentValue,
        0,
      ),
      mostCommonMsg: maxBy(Object.keys(this.msgCount), (k) => this.msgCount[k]),
      pingRTT: {
        max: Math.max(...this.pingRTT),
        median: median(this.pingRTT),
        min: Math.min(...this.pingRTT),
      },
      rtt: {
        max: Math.max(...allRTTs),
        median: median(allRTTs),
        min: Math.min(...allRTTs),
        slowestMsg: maxBy(Object.keys(this.msgRTT), (k) => Math.max(...this.msgRTT[k])),
      },
    };
  }

  public isPingHigh() {
    const rollingMax = Math.max(...this.rollingPingRTT);
    const rollingMedian = median(this.rollingPingRTT);
    if (rollingMax >= POOR_CONNECTION_EXTREME_PING_THRESHOLD) {
      return true;
    }
    if (
      rollingMax >= POOR_CONNECTION_MAX_PING_THRESHOLD &&
      rollingMedian &&
      rollingMedian >= POOR_CONNECTION_MEDIAN_PING_THRESHOLD
    ) {
      return true;
    }
    return false;
  }

  public isPingLow() {
    const rollingMax = Math.max(...this.rollingPingRTT);
    const rollingMedian = median(this.rollingPingRTT);
    if (
      rollingMax < GOOD_CONNECTION_MAX_PING_THRESHOLD &&
      rollingMedian &&
      rollingMedian < GOOD_CONNECTION_MEDIAN_PING_THRESHOLD
    ) {
      return true;
    }
    return false;
  }
}
